/**
 * Swipe helpers for the mobile nav drawer.
 *
 * Open: rightward swipe that starts in the left portion of the viewport
 * (not the system bezel — Android/iOS reserve ~20–30px for Back).
 * Close: leftward swipe while the drawer is open.
 *
 * Gating is by touch capability, not CSS width alone: phones with an
 * attached keyboard, landscape tablets, or "Desktop site" can exceed
 * 767px while still needing the gesture.
 */

/** Fraction of viewport width where an open swipe may begin (from the left). */
export const OPEN_START_FRACTION = 0.4;

/** Minimum horizontal travel (px) to commit open/close. */
export const MIN_SWIPE_DX_PX = 60;

/**
 * Max |dy|/|dx| for a mostly-horizontal swipe. Above this we treat the
 * gesture as a vertical scroll and ignore it.
 */
export const MAX_VERTICAL_RATIO = 0.6;

export type NavigatorLike = {
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
};

export type EdgeSwipeAction = 'open' | 'close';

export type TouchPoint = { clientX: number; clientY: number };

/** True when the environment can deliver touch events (phone, tablet, touch laptop). */
export function hasTouchCapability(
  nav: NavigatorLike | null | undefined = typeof navigator !== 'undefined' ? navigator : undefined,
  win: { ontouchstart?: unknown; matchMedia?: (q: string) => { matches: boolean } } | null | undefined =
    typeof window !== 'undefined' ? window : undefined
): boolean {
  if (nav && Number(nav.maxTouchPoints ?? 0) > 0) return true;
  if (win && 'ontouchstart' in win) return true;
  if (typeof win?.matchMedia === 'function') {
    try {
      if (win.matchMedia('(pointer: coarse)').matches) return true;
      if (win.matchMedia('(hover: none)').matches) return true;
    } catch {
      // ignore
    }
  }
  return false;
}

/** Left band [0, viewportWidth * OPEN_START_FRACTION] for open-swipe starts. */
export function openStartMaxX(
  viewportWidth: number,
  fraction: number = OPEN_START_FRACTION
): number {
  const w = Number.isFinite(viewportWidth) && viewportWidth > 0 ? viewportWidth : 0;
  return w * fraction;
}

export function canStartOpenSwipe(
  touchX: number,
  viewportWidth: number,
  fraction: number = OPEN_START_FRACTION
): boolean {
  if (touchX < 0) return false;
  return touchX <= openStartMaxX(viewportWidth, fraction);
}

/**
 * Global lock while Facility View (or Designer) is mid-drag. Ref-counted so
 * overlapping owners release independently. Also toggles
 * `body.tilly-menu-swipe-locked` for CSS / debugging.
 */
const menuSwipeLocks = new Set<string>();

function syncMenuSwipeLockBodyClass(): void {
  if (typeof document === 'undefined' || !document.body) return;
  document.body.classList.toggle('tilly-menu-swipe-locked', menuSwipeLocks.size > 0);
}

/** Silence nav edge-swipe while `id` holds the lock (e.g. vessel transfer drag). */
export function lockMenuSwipe(id: string): void {
  if (!id) return;
  menuSwipeLocks.add(id);
  syncMenuSwipeLockBodyClass();
}

export function unlockMenuSwipe(id: string): void {
  if (!id) return;
  menuSwipeLocks.delete(id);
  syncMenuSwipeLockBodyClass();
}

export function isMenuSwipeLocked(): boolean {
  return menuSwipeLocks.size > 0;
}

/** Jest helper — do not call from product code. */
export function resetMenuSwipeLockForTests(): void {
  menuSwipeLocks.clear();
  syncMenuSwipeLockBodyClass();
}

/**
 * Blockers that must silence the open/close gesture: scanner overlay, Bootstrap
 * / custom modals, form fields, Facility Designer canvas, and sideways-scrolling
 * ancestors that are not already at their left edge.
 *
 * Home is Facility View. The FV canvas covers most of the left 40% band on
 * phones, so it must NOT be a blanket blocker (that killed swipe-open after
 * #178). Empty-canvas open-swipe is allowed; vessel press / active drag hold
 * `lockMenuSwipe` instead (React/Konva handlers run before the document
 * `touchstart` listener, so the lock is set first). Designer stays blocked:
 * shape drag locks only on Konva `dragstart`, which is too late to silence an
 * open-swipe that began on a shape.
 */
export function isGestureBlockedTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return false;

  if (
    target.closest(
      [
        '.kso',
        '[data-testid="keg-scan-overlay"]',
        '.flow-scan-modal',
        '.modal.show',
        '[aria-modal="true"]',
        '.facility-location-drawer',
        '.facility-location-drawer__backdrop',
        '[data-testid="design-canvas"]',
        'input',
        'textarea',
        'select',
        '[contenteditable="true"]',
      ].join(', ')
    )
  ) {
    return true;
  }

  return hasHorizontalScrollAncestor(target);
}

/**
 * True when a horizontally overflowing scroller has already been panned away
 * from the left edge. At `scrollLeft === 0`, rightward open-swipe is allowed
 * (e.g. Facility View canvas on a phone); once scrolled in, prefer native
 * horizontal scroll over opening the menu.
 */
function hasHorizontalScrollAncestor(el: Element): boolean {
  let node: Element | null = el;
  while (node && node !== document.documentElement) {
    if (node instanceof HTMLElement) {
      const style = typeof window !== 'undefined' ? window.getComputedStyle(node) : null;
      const overflowX = style?.overflowX ?? '';
      if (
        (overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') &&
        node.scrollWidth > node.clientWidth + 1 &&
        node.scrollLeft > 0
      ) {
        return true;
      }
    }
    node = node.parentElement;
  }
  return false;
}

/** True when a modal/scanner/drag that should disable the gesture is active. */
export function isOverlayBlockingMenuSwipe(root: ParentNode = document): boolean {
  if (isMenuSwipeLocked()) return true;
  return !!root.querySelector(
    [
      '.kso',
      '[data-testid="keg-scan-overlay"]',
      '.flow-scan-modal',
      '.modal.show',
      '[aria-modal="true"]',
      '.facility-location-drawer',
      '.facility-view__canvas.is-dragging',
      'body.tilly-menu-swipe-locked',
    ].join(', ')
  );
}

export function classifyEdgeSwipe(
  start: TouchPoint,
  end: TouchPoint,
  menuOpen: boolean,
  viewportWidth: number
): EdgeSwipeAction | null {
  const dx = end.clientX - start.clientX;
  const dy = end.clientY - start.clientY;
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);

  if (absDx < MIN_SWIPE_DX_PX) return null;
  if (absDx === 0) return null;
  if (absDy / absDx > MAX_VERTICAL_RATIO) return null;

  if (!menuOpen) {
    if (dx > 0 && canStartOpenSwipe(start.clientX, viewportWidth)) return 'open';
    return null;
  }

  // Menu open: leftward swipe closes (start may be anywhere on screen / drawer).
  if (dx < 0) return 'close';
  return null;
}
